import type { AdminOrgDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandPreview } from './BrandPreview';
import { CompanySourceBadge } from './CompanySourceBadge';
import { EditCompanyDialog } from './EditCompanyDialog';
import { LogoPicker } from './LogoPicker';
import { InfoRow, SectionCard, Value } from './SectionCard';
import { placeText } from '../../../utils/place';
import { PiiText } from '../../pickers/_components/PiiText';
import { logoErrorKey, uploadOrgLogo } from '../_lib/logoUpload';
import { ownerCodeState } from '../_lib/ownerCodeState';
import { useOrgWrite } from '../_lib/useOrgWrite';

/**
 * «Обзор» (AUT-403), after `companies-detail.html?tab=overview`: KPI tiles,
 * «Профиль компании» (+ «Редактировать» and logo replace), «Контакты в
 * отчёте», and the brand preview.
 *
 * The mockup's «Отчётов за 30 дней» / «Активных ссылок» tiles wait for
 * company reports (CP-05). Edit is hidden without `view_pii` (the contacts
 * arrive masked — see `EditCompanyDialog`) and for a terminated company
 * (the API refuses: final). The logo replace is hidden when terminated.
 */
const Kpi = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint: string;
}): ReactElement => (
  <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {hint}
    </Typography>
  </Paper>
);

const Kpis = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  const state = ownerCodeState(org);
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Kpi
        label={t('companies.overview.kpi.members')}
        value={t('companies.overview.kpi.membersValue', {
          active: org.activeMembers,
          max: org.maxMembers,
        })}
        hint={t('companies.overview.kpi.membersHint')}
      />
      <Kpi
        label={t('companies.overview.kpi.owner')}
        value={org.owner?.name ?? t('companies.codes.owner.ownerNone')}
        hint={t(`companies.overview.kpi.ownerHint.${state}`)}
      />
    </Box>
  );
};

const Profile = ({
  org,
  onEdit,
}: {
  org: AdminOrgDto;
  onEdit: (() => void) | null;
}): ReactElement => {
  const { t } = useTranslation();
  const write = useOrgWrite();
  const ns = 'companies.overview';
  const upload = (file: File): void =>
    void write.run(() => uploadOrgLogo(org.id, file), 'companies.logo.uploaded', logoErrorKey);
  const action = onEdit ? (
    <Button size="small" variant="outlined" onClick={onEdit}>
      {t(`${ns}.edit`)}
    </Button>
  ) : undefined;

  return (
    <SectionCard title={t(`${ns}.profile`)} action={action}>
      <InfoRow label={t('companies.form.name')}>
        <Value>{org.name}</Value>
      </InfoRow>
      <InfoRow label={t('companies.overview.legalName')}>
        <Value>{org.legalName}</Value>
      </InfoRow>
      <InfoRow label={t('companies.columns.unp')} hint={t(`${ns}.unpHint`)}>
        <Value mono>{org.unp}</Value>
      </InfoRow>
      <InfoRow label={t('companies.form.city')}>
        <Value>{placeText(org)}</Value>
      </InfoRow>
      <InfoRow label={t('companies.columns.source')}>
        <CompanySourceBadge source={org.source} />
      </InfoRow>
      <InfoRow label={t('companies.form.maxMembers')}>
        <Value mono>{String(org.maxMembers)}</Value>
      </InfoRow>
      <InfoRow label={t(`${ns}.logo`)}>
        {org.status === 'terminated' ? (
          <Value>{org.logoUrl ? t(`${ns}.logoSet`) : t(`${ns}.logoNone`)}</Value>
        ) : (
          <Box sx={{ flex: 1 }}>
            <LogoPicker
              onPick={upload}
              busy={write.busy}
              actionLabel={t(org.logoUrl ? 'companies.logo.replace' : 'companies.logo.choose')}
            />
          </Box>
        )}
      </InfoRow>
    </SectionCard>
  );
};

const Contacts = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('companies.overview.contacts')}
      sub={t('companies.overview.contactsHint')}
    >
      <InfoRow label={t('companies.form.phone')}>
        <PiiText value={org.contacts.phone} mono />
      </InfoRow>
      <InfoRow label={t('companies.form.telegram')}>
        <PiiText
          value={org.contacts.telegram ? `@${org.contacts.telegram.replace(/^@/, '')}` : null}
        />
      </InfoRow>
      <InfoRow label={t('companies.form.site')}>
        <Value>{org.contacts.site}</Value>
      </InfoRow>
    </SectionCard>
  );
};

export const OverviewTab = ({ org }: { org: AdminOrgDto }): ReactElement => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const write = useOrgWrite();
  const canEdit = org.piiRevealed && org.status !== 'terminated';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Kpis org={org} />
      {!org.piiRevealed ? <Alert severity="info">{t('companies.overview.editNoPii')}</Alert> : null}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Profile org={org} onEdit={canEdit ? () => setEditing(true) : null} />
          <Contacts org={org} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('companies.overview.brandTitle')}
          </Typography>
          <BrandPreview
            name={org.name}
            city={placeText(org) ?? ''}
            logoUrl={org.logoUrl}
            phone={org.contacts.phone ?? ''}
            telegram={org.contacts.telegram ?? ''}
            site={org.contacts.site ?? ''}
          />
          <Typography variant="caption" color="text.secondary">
            {t('companies.overview.brandNote')}
          </Typography>
        </Box>
      </Box>
      {canEdit ? (
        <EditCompanyDialog
          org={org}
          open={editing}
          write={write}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </Box>
  );
};
